import {
    EuiButtonEmpty,
    EuiButtonIcon,
    EuiCheckbox,
    EuiFlexGrid,
    EuiFlexGroup,
    EuiFlexItem,
    EuiGlobalToastList,
} from "@elastic/eui";
import React, { useCallback, useContext, useState } from "react";
import { useQueryClient } from "react-query";

import GlobalToastContext from "../../contextProviders/GlobalToastProvider";
import { KindForTable, MutationType } from "../../utils/types";
import { useProductDetailMutations } from "./utils";

interface EditStrainInfoProps {
    productId: string;
}

export const EditKindBadges = ({ productId }: EditStrainInfoProps) => {
    const { update_product_mutation } = useProductDetailMutations("kinds");
    const queryClient = useQueryClient();
    const data = queryClient.getQueryData(["kinds", productId]) as KindForTable;
    const { toasts, addErrorToast, addToastHandler, removeToast } = useContext(GlobalToastContext);
    const updateProduct = useCallback(
        (product: KindForTable, updateType: MutationType, objectType?: string) => {
            update_product_mutation.mutate(product, {
                onSuccess: () => {
                    queryClient.invalidateQueries(["kinds", product.id]);
                    queryClient.setQueryData(["kinds", product.id], product);
                    addToastHandler(updateType, objectType);
                },
                onError: (error: any) => {
                    queryClient.refetchQueries(["kinds", product.id]);
                    addErrorToast(error);
                },
            });
        },
        [addErrorToast, addToastHandler, queryClient, update_product_mutation]
    );

    const [editing, setEditing] = useState(false);

    const [checked, setChecked] = useState({
        cbd: data?.c,
        hybrid: data?.h,
        indica: data?.i,
        sativa: data?.s,
    });

    const save = () => {
        setEditing(false);
        let newProduct = data;
        newProduct.c = checked.cbd;
        newProduct.h = checked.hybrid;
        newProduct.i = checked.indica;
        newProduct.s = checked.sativa;
        updateProduct(newProduct, MutationType.Edited, "Product info");
    };

    const closeEditor = () => {
        setChecked({
            cbd: data?.c,
            hybrid: data?.h,
            indica: data?.i,
            sativa: data?.s,
        });
        setEditing(false);
    };

    return (
        <div style={{ padding: 4 }}>
            <EuiFlexGroup style={{ marginTop: "-0.4em" }}>
                {editing ? (
                    <EuiFlexGrid columns={3}>
                        <EuiFlexItem>
                            <EuiCheckbox
                                label="CBD"
                                checked={checked.cbd}
                                onChange={(e) =>
                                    setChecked((prevChecked) => ({
                                        ...prevChecked,
                                        cbd: !checked.cbd,
                                    }))
                                }
                                id="cbd"
                            />
                        </EuiFlexItem>
                        <EuiFlexItem>
                            <EuiCheckbox
                                label="Hybrid"
                                checked={checked.hybrid}
                                onChange={(e) =>
                                    setChecked((prevChecked) => ({
                                        ...prevChecked,
                                        hybrid: !checked.hybrid,
                                    }))
                                }
                                id="hybrid"
                            />
                        </EuiFlexItem>
                        <EuiFlexItem>
                            <EuiButtonIcon
                                aria-label="saveKind"
                                onClick={save}
                                iconType="save"
                                iconSize="l"
                                isDisabled={false}
                            />
                        </EuiFlexItem>
                        <EuiFlexItem>
                            <EuiCheckbox
                                label="Indica"
                                checked={checked.indica}
                                onChange={(e) =>
                                    setChecked((prevChecked) => ({
                                        ...prevChecked,
                                        indica: !checked.indica,
                                    }))
                                }
                                id="indica"
                            />
                        </EuiFlexItem>
                        <EuiFlexItem>
                            <EuiCheckbox
                                label="Sativa"
                                checked={checked.sativa}
                                onChange={(e) =>
                                    setChecked((prevChecked) => ({
                                        ...prevChecked,
                                        sativa: !checked.sativa,
                                    }))
                                }
                                id="Sativa"
                            />
                        </EuiFlexItem>
                        <EuiFlexItem>
                            <EuiButtonIcon
                                onClick={closeEditor}
                                iconType="editorUndo"
                                color="danger"
                                iconSize="l"
                                isDisabled={false}
                            />
                        </EuiFlexItem>
                    </EuiFlexGrid>
                ) : (
                    <>
                        {Object.keys(checked).map((key) => {
                            if (checked[key as keyof object]) {
                                return (
                                    <EuiFlexItem style={{ maxWidth: "30px", height: "30px" }} key={key}>
                                        {key}
                                    </EuiFlexItem>
                                );
                            }
                            return <></>;
                        })}
                        {!checked.cbd && !checked.hybrid && !checked.indica && !checked.sativa ? (
                            <EuiButtonEmpty
                                style={{ marginTop: "0.5em" }}
                                onClick={() => setEditing(true)}
                                size="m"
                                iconType="plus"
                                isDisabled={false}
                            >
                                Add badges
                            </EuiButtonEmpty>
                        ) : (
                            <EuiButtonEmpty
                                style={{ marginTop: "0.5em" }}
                                onClick={() => setEditing(true)}
                                size="m"
                                iconType="pencil"
                                isDisabled={false}
                            />
                        )}
                    </>
                )}
            </EuiFlexGroup>
            <EuiGlobalToastList toasts={toasts} dismissToast={removeToast} toastLifeTimeMs={6000} side="left" />
        </div>
    );
};
